import {
  ChakraBox,
  ChakraFlex,
  FAIconName,
  ParticipantCard,
  Text,
  UniversalSelect,
} from '@equip.health/ui';
import type { AvatarUserType } from '@equip.health/ui/lib/types/src/components/Avatar';
import { debounce } from 'lodash';
import { useMemo, useRef, useState } from 'react';
import { useProvidersSearchData } from '~/components/maud/inquiries/InquiryDetail/useProvidersSearchData';
import { getMenuOptions } from '~/components/maud/inquiries/InquiryDetail/viewUtil';
import { capitalizeFirstLetter } from '~/lib/common';
import { ParticipantTypes } from '~/lib/constants/schedule.constants';

const appointmentInputWidth = '30%';
const participantCardPadding = '4px';

interface Participant {
  id: string;
  isChecked: boolean;
  primaryText: string;
  secondaryText: string;
  isEligibleToHost?: boolean;
  type?: string;
}

interface ScheduleUserBase {
  externalId: string;
  name: string;
  type?: string;
  relationToPatient?: string;
  isHost?: boolean;
}

export interface ParticipantsListProps {
  topLevelId: string;
  hostAttendee?: Participant;
  patientAttendee?: Participant;
  supportAttendees?: Participant[];
  providerAttendees?: Participant[];
  updatedAttendees?: ScheduleUserBase[]; // For backward compatibility with RescheduleSlotSelectionStep
  onParticipantCardClick?: (
    participant: Participant | ScheduleUserBase,
    userType: ParticipantTypes,
  ) => void;
  getKebabOptions?: (participant: Participant | ScheduleUserBase) => unknown;
  getCaption?: (attendee: Participant | ScheduleUserBase) => string;
  height?: string;
  showParticipants?: boolean;
  handleParticipantAdded: (participant: GetProviderByIdResponse) => void;
  shouldEditAllAppointments?: boolean;
}

const ParticipantsList = ({
  topLevelId,
  hostAttendee,
  patientAttendee,
  supportAttendees = [],
  providerAttendees = [],
  updatedAttendees = [],
  onParticipantCardClick,
  getKebabOptions,
  getCaption,
  handleParticipantAdded,
  height = '100%',
  showParticipants = true,
  shouldEditAllAppointments,
}: ParticipantsListProps) => {
  const [inputValue, setInputValue] = useState('');
  const [pageNumber, setPageNumber] = useState<number>(1);

  const providers = useProvidersSearchData({
    addCalendarProfile: true,
    pageSize: 20,
  });

  const updatedProviderAttendees = useMemo(
    () =>
      shouldEditAllAppointments
        ? providerAttendees?.filter((attendee) => attendee.isChecked)
        : providerAttendees,
    [shouldEditAllAppointments, providerAttendees],
  );
  const updatedSupportAttendees = useMemo(
    () =>
      shouldEditAllAppointments
        ? supportAttendees?.filter((attendee) => attendee.isChecked)
        : supportAttendees,
    [shouldEditAllAppointments, supportAttendees],
  );

  const patientParticipant = useMemo(() => {
    if (!patientAttendee) return null;
    if (shouldEditAllAppointments && !patientAttendee.isChecked) return null;
    return patientAttendee;
  }, [patientAttendee, shouldEditAllAppointments]);

  const participantUserIds: string[] = [
    hostAttendee?.id,
    ...providerAttendees.map((p) => p?.id),
  ];

  const menuOptions = useMemo(
    () =>
      getMenuOptions(topLevelId, providers)
        .filter((provider) => !participantUserIds.includes(provider.userId))
        .map((option) => {
          return { ...option, isDisabled: !option.calendarId };
        }),
    [providers],
  );

  const debouncedFetchOptions = useRef(
    debounce((searchTerm: string, pageNumber: number) => {
      providers.handleSearch(searchTerm, pageNumber);
    }, 500),
  ).current;

  const loadMoreOptions = () => {
    if (providers.totalCount === providers.dataList.length) {
      return;
    }
    const newPageNumber = pageNumber + 1;
    debouncedFetchOptions(inputValue, newPageNumber);
    setPageNumber(newPageNumber);
  };

  if (!showParticipants) {
    return null;
  }

  const shouldUseUpdatedAttendees = updatedAttendees.length > 0;

  const renderParticipantCard = (
    participant: Participant | ScheduleUserBase,
    isHost = false,
    kebabItems = null,
  ) => {
    const isParticipantType = (
      p: Participant | ScheduleUserBase,
    ): p is Participant => {
      return 'primaryText' in p;
    };

    const getParticipantId = (p: Participant | ScheduleUserBase): string => {
      return isParticipantType(p) ? p.id : p.externalId;
    };

    const getParticipantName = (p: Participant | ScheduleUserBase): string => {
      return isParticipantType(p) ? p.primaryText : p.name;
    };

    const getParticipantTypeString = (
      p: Participant | ScheduleUserBase,
    ): AvatarUserType => {
      if (isHost || p.type?.toLowerCase() === ParticipantTypes.PROVIDER) {
        return ParticipantTypes.PROVIDER;
      }
      return ParticipantTypes.PATIENT;
    };

    const getParticipantTypeEnum = (
      p: Participant | ScheduleUserBase,
    ): ParticipantTypes => {
      if (isParticipantType(p)) {
        return (p.type as ParticipantTypes) ?? ParticipantTypes.PROVIDER;
      }

      if (p.type?.toLowerCase() === ParticipantTypes.PATIENT) {
        return ParticipantTypes.PATIENT;
      }

      if (p.relationToPatient) {
        return ParticipantTypes.SUPPORT;
      }

      return ParticipantTypes.PROVIDER;
    };

    let caption = '';

    const isParticipantTypedParticipant = isParticipantType(participant);

    if (getCaption) {
      caption = getCaption(participant);
    } else if (isParticipantTypedParticipant) {
      caption = participant.secondaryText;
    }

    const participantId = getParticipantId(participant);
    const participantName = getParticipantName(participant);

    return (
      <ChakraBox
        cursor={shouldEditAllAppointments ? 'not-allowed' : 'pointer'}
        key={participantId}
        padding={participantCardPadding}
      >
        <ParticipantCard
          caption={caption}
          id={`${topLevelId}__${participantId}`}
          isHost={isHost}
          isSelected={
            isParticipantTypedParticipant ? participant.isChecked : false
          }
          {...(!isHost &&
            onParticipantCardClick &&
            !shouldEditAllAppointments && {
              onCardClick: () => {
                const participantType = getParticipantTypeEnum(participant);
                onParticipantCardClick(participant, participantType);
              },
            })}
          {...(!shouldEditAllAppointments && kebabItems && { kebabItems })}
          participantName={capitalizeFirstLetter(participantName)}
          participantType={getParticipantTypeString(participant)}
        />
      </ChakraBox>
    );
  };

  const loadOptions = (inputValue: string) => {
    const newPageNumber = 1;
    debouncedFetchOptions(inputValue, newPageNumber);
    setPageNumber(newPageNumber);
    setInputValue(inputValue);
  };

  return (
    <ChakraFlex
      direction="column"
      height={height}
      width={appointmentInputWidth}
    >
      <ChakraBox marginBottom="12px">
        <Text color="neutral.secondary" marginBottom="6px" variant="caption">
          Participants
        </Text>

        {!shouldEditAllAppointments && (
          <UniversalSelect
            hideTags
            id={`${topLevelId}__select-provider`}
            isClearable={false}
            isLoading={false}
            isSearchable
            isSingleSelect
            leftIcon={FAIconName.magnifyingGlassRegular}
            modalId={`${topLevelId}__modal-modal`}
            onChange={handleParticipantAdded}
            onInputChange={loadOptions}
            onMenuScrollToBottom={loadMoreOptions}
            options={menuOptions || []}
            placeholder="Add providers"
          />
        )}
      </ChakraBox>
      <ChakraBox height="100%" overflow="auto">
        {shouldUseUpdatedAttendees ? (
          updatedAttendees?.map((attendee) =>
            renderParticipantCard(attendee, attendee.isHost),
          )
        ) : (
          <>
            {hostAttendee && renderParticipantCard(hostAttendee, true)}
            {patientParticipant && renderParticipantCard(patientParticipant)}
            {updatedSupportAttendees?.map((attendee) =>
              renderParticipantCard(attendee),
            )}
            {updatedProviderAttendees?.map((attendee) =>
              renderParticipantCard(
                attendee,
                false,
                getKebabOptions ? getKebabOptions(attendee) : null,
              ),
            )}
          </>
        )}
      </ChakraBox>
    </ChakraFlex>
  );
};

export default ParticipantsList;
